import { forwardRef, useId, type SelectHTMLAttributes } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  options: SelectOption[];
  /** Optional placeholder shown as first disabled option */
  placeholder?: string;
  error?: string;
  /** Render at smaller size (px-3 py-2 text-sm). Default: false */
  compact?: boolean;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, placeholder, error, compact = false, className = '', id: externalId, ...props }, ref) => {
    const generatedId = useId();
    const id = externalId || generatedId;
    const errorId = `${id}-error`;

    const sizeClasses = compact ? 'px-3 py-2 text-sm' : 'px-4 py-3';

    return (
      <div>
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={id}
          className={`
            w-full ${sizeClasses} border border-gray-300 dark:border-gray-600 rounded-lg
            focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition
            bg-white dark:bg-gray-700 text-gray-900 dark:text-white
            ${error ? 'border-red-500 dark:border-red-400' : ''}
            ${className}
          `.trim()}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={!!error}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={errorId} className="text-red-500 text-sm mt-1" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
export default Select;
